import styles from '@/components/Partnership/form.module.scss';
import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Controller, useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import api from '@/api/intercepted';
import { useTranslation } from 'next-i18next';
import * as Sentry from "@sentry/nextjs";
import SearchableSelect from "@/components/ui/SearchableSelect";
import useFederalDistricts from '@/components/common/useFederalDistricts';

const SALES_CHANNELS = ['HoReCa', 'Розница', 'Опт'];
const RUSSIA_COUNTRY = 'Россия';
const OTHER_COUNTRY = 'Другое';
const COUNTRY_VALUES = [RUSSIA_COUNTRY, OTHER_COUNTRY];

const variants = {
  initial: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 1.3,
      delay: 1,
    },
  },
  hidden: { opacity: 0 },
};

const RequestPriceForm = ({ requestData, onSuccess, onClose }) => {
  const [errors, setErrors] = useState(false);
  const [success, setSuccess] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors: formErrors },
  } = useForm({
    defaultValues: {
      sales_channel: '',
      country_type: '',
      country_other: '',
      federal_district: '',
    },
  });
  const { t } = useTranslation(['common', 'products']);
  const federalDistricts = useFederalDistricts();
  const countryType = watch('country_type');
  const countryOptions = useMemo(
    () => [
      { value: RUSSIA_COUNTRY, label: t('Россия') },
      { value: OTHER_COUNTRY, label: t('Другое') },
    ],
    [t]
  );

  useEffect(() => {
    if (countryType === RUSSIA_COUNTRY) {
      setValue('country_other', '');
    } else if (countryType === OTHER_COUNTRY) {
      setValue('federal_district', '');
    }
  }, [countryType, setValue]);

  const onSubmit = async data => {
    setErrors(false);
    setSuccess(false);
    const formData = new FormData();

    data.phone = data.phone.replace(/[^\d+]/g, '');

    const { country_type, country_other, federal_district, ...rest } = data;
    const submitData = {
      ...requestData,
      ...rest,
      country: country_type === RUSSIA_COUNTRY ? RUSSIA_COUNTRY : country_other?.trim(),
    };
    if (country_type === RUSSIA_COUNTRY) {
      submitData.federal_district = federal_district;
    }

    Object.entries(submitData).forEach(([key, value]) => {
      formData.append(key, value);
    });

    try {
      const res = await api.post('/v1/api/products/request/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.status === 201) {
        resetForm();
        setSuccess(true);
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      Sentry.captureException(error);
      setErrors(true);
    }
  };

  const resetForm = () => {
    reset({
      name: '',
      phone: '',
      email: '',
      sales_channel: '',
      country_type: '',
      country_other: '',
      federal_district: '',
      city: '',
      agreement: false,
    });
  };

  return (
      <motion.form
          className={styles.wrapper}
          onSubmit={handleSubmit(onSubmit)}
          initial="initial"
          animate="visible"
          exit="hidden"
          variants={variants}
      >
        {errors && (
            <div className={clsx(styles.formLine, styles.formLineErrors)}>
              {t(
                  'Произошла ошибка при отправке формы. Пожалуйста, проверьте правильно ли Вы заполнили все поля.'
              )}
            </div>
        )}
        {success && (
            <div className={clsx(styles.formLine, styles.formLineSuccess)}>
              {t('Заявка успешно отправлена.')}
            </div>
        )}
        <div className={clsx(styles.formLine, formErrors.phone && styles.error)}>
          <input
              type="text"
              placeholder={t('Телефон')}
              {...register('phone', {
                required: true,
                maxLength: 256,
                onChange: (event) => {
                  const {value} = event.target;
                  event.target.value = value.replace(/[^\d\(\)\-—+\s]/g, '');
                }
              })}
          />
        </div>
        <div className={clsx(styles.formLine, formErrors.email && styles.error)}>
          <input
              type="email"
              placeholder={t('E-mail')}
              {...register('email', {required: true, maxLength: 256})}
          />
        </div>
        <div className={clsx(styles.formLine, formErrors.name && styles.error)}>
          <input
              type="text"
              placeholder={t('Имя')}
              {...register('name', {required: true, maxLength: 256})}
          />
        </div>
        <div className={clsx(styles.formLine, formErrors.sales_channel && styles.error)}>
          <Controller
              name="sales_channel"
              control={control}
              rules={{
                required: true,
                validate: value => SALES_CHANNELS.includes(value),
              }}
              render={({field}) => (
                  <SearchableSelect
                      options={SALES_CHANNELS}
                      placeholder={t('Канал продаж')}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      noResultsText={t('Ничего не найдено')}
                  />
              )}
          />
        </div>
        <div className={clsx(styles.formLine, formErrors.country_type && styles.error)}>
          <Controller
            name="country_type"
            control={control}
            rules={{
              required: true,
              validate: value => COUNTRY_VALUES.includes(value),
            }}
            render={({ field }) => (
              <SearchableSelect
                options={countryOptions}
                labelKey="label"
                valueKey="value"
                placeholder={t('Страна')}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                name={field.name}
                noResultsText={t('Ничего не найдено')}
                readOnly
              />
            )}
          />
        </div>
        {countryType === OTHER_COUNTRY && (
          <div className={clsx(styles.formLine, formErrors.country_other && styles.error)}>
            <input
              type="text"
              placeholder={t('Страна')}
              {...register('country_other', {
                required: countryType === OTHER_COUNTRY,
                maxLength: 256,
              })}
            />
          </div>
        )}
        {countryType === RUSSIA_COUNTRY && (
          <div className={clsx(styles.formLine, formErrors.federal_district && styles.error)}>
            <Controller
              name="federal_district"
              control={control}
              rules={{
                required: countryType === RUSSIA_COUNTRY,
                validate: value =>
                  countryType !== RUSSIA_COUNTRY ||
                  federalDistricts.some(district => district.title === value),
              }}
              render={({ field }) => (
                <SearchableSelect
                  options={federalDistricts}
                  placeholder={t('Федеральный округ')}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  noResultsText={t('Ничего не найдено')}
                />
              )}
            />
          </div>
        )}
        <div className={clsx(styles.formLine, formErrors.city && styles.error)}>
          <input
              type="text"
              placeholder={t('Город')}
              {...register('city', {required: true, maxLength: 256})}
          />
        </div>
        <div className={clsx(styles.formAgreement, formErrors.agreement && styles.error)}>
          <input
              className={styles.checkbox}
              id="policy"
              type="checkbox"
              {...register('agreement', {required: true})}
          />
          <label htmlFor="policy">{t(
              'Я согласен на обработку персональных данных на условиях '
          )}<a
              href="/privacy-policy" target="_blank">{t(
              'Политики в отношении обработки персональных данных'
          )}
          </a></label>
        </div>
        <div className={styles.actions}>
          <button type="submit" className={clsx(styles.button, styles.buttonSend)}>
            {t('Получить прайс')}
          </button>
        </div>
      </motion.form>
  );
};

export default RequestPriceForm;
